// js/core/cost-warnings.js — the holes a job's cost can have, named.
//
// Every figure on Job Costs and the P&L is exactly what the records say. That
// is the problem this module exists for: a record that is missing, doubled or
// impossible still adds up to a clean total, and a clean total reads as a
// correct one. Each function below looks for ONE such hole and, when it finds
// it, says who / what / how much / when.
//
// Three rules, the same for every detector:
//   1. It never moves a number. Which side of a disagreement is true is a fact
//      only the owner has; the engine reports, the owner decides.
//   2. Names and units come from the tenant's own records (profiles, the
//      production catalogue, the rental's label). Nothing here knows a trade.
//   3. A warning is plain data — { key, severity, group, params } — so the
//      screen, the P&L and the PDF print the same sentence from the same
//      object. `key` is an i18n key; `params` are RAW values (dates as
//      'YYYY-MM-DD', money and hours as numbers) and formatWarning() below is
//      the one place they are turned into text.
//
//   severity  'warn'  money is probably missing or doubled
//             'info'  worth a look; often legitimate
//   group     'material' | 'labour' | 'equipment' | 'trucking'
//                                      where on the statement the money sits
//             'contract'               the earned side (production, percent)
//
// Pure: no DOM, no fetch, no clock — `today` is always handed in.

import { weekMondayOf } from './timesheet-activity.js';
import { localDateStr } from './period.js';
import { scaffoldOverlaps } from './scaffold-rental.js';
import { finishedBy } from './job-value.js';
import { dayStats, outlierOf, MEDIAN_FACTOR } from './production-outliers.js';

/** More hours than this for one person in one day is flagged — never capped. */
export const DAY_HOURS_LIMIT = 16;
/**
 * An open rental is flagged once a full month of rent (the unit it is priced
 * in) has gone by with nothing reported on the job.
 */
export const IDLE_RENTAL_DAYS = 31;
/** Same month the equipment day-math divides a monthly rate by. */
const DAYS_PER_MONTH = 30.4375;
/** contract_line_progress backfill sentinel (js/core/period.js). */
const EPOCH = '1900-01-01';

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const dayNum = (d) => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10)) / 86400000;
const addDays = (d, n) => new Date((dayNum(d) + n) * 86400000).toISOString().slice(0, 10);
const hoursOf = (e) => num(e.regular_hours) + num(e.overtime_hours);
const widen = (g, d) => {
  if (!g.from || d < g.from) g.from = d;
  if (!g.to || d > g.to) g.to = d;
};
// One record's raw facts as one string — a warning's `basis` (see
// warningFingerprint): what it counted or measured, as the records hold it.
const fact = (...v) => v.map(x => (x == null ? '' : String(x))).join('|');

/** timestamptz → 'YYYY-MM-DD' on the COMPANY's calendar, not the viewer's. */
export function dateInZone(ts, timeZone) {
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d)) return null;
  if (timeZone) {
    try {
      return new Intl.DateTimeFormat('en-CA', {
        timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
    } catch (_) { /* unknown zone on file → the viewer's calendar */ }
  }
  return localDateStr(ts);
}

const linesByReport = (prodLines) => {
  const map = {};
  (prodLines || []).forEach(l => {
    if (l.production_report_id) (map[l.production_report_id] = map[l.production_report_id] || []).push(l);
  });
  return map;
};

// What one report says was installed, as printable items. Line items first
// (the catalogue's own name and unit); a report that has none still carries
// its quantity in the three retired columns, which only have a label key.
const LEGACY = [['blocks_installed', 'jc.blocks'], ['bricks_installed', 'jc.bricks'], ['firestop_lf', 'jc.firestop']];
function reportItems(report, lines) {
  if (lines && lines.length) {
    return lines.filter(l => num(l.quantity) > 0).map(l => ({
      id: l.material_type_id, qty: num(l.quantity),
      name: l.material_types?.name || '', unit: l.material_types?.unit || '' }));
  }
  return LEGACY.filter(([col]) => num(report[col]) > 0)
    .map(([col, labelKey]) => ({ id: labelKey, qty: num(report[col]), labelKey }));
}
function addItems(bag, items) {
  items.forEach(it => {
    const b = bag[it.id] || (bag[it.id] = { ...it, qty: 0 });
    b.qty += it.qty;
  });
}
const itemList = (bag) => Object.values(bag).map(({ id, ...rest }) => rest);

/**
 * Production reported by a crew that is NOT a subcontract crew, in a week
 * where the job has no labour on file at all.
 *
 * An in-house crew's production is deliberately not priced per unit — its cost
 * arrives through the timesheet. So when the week has no timesheet hours on
 * the job from anyone (and no Labour cost typed by the office), that work
 * earns and consumes material at a labour cost of zero. The usual cause was a
 * sub crew's week filed with the crew picker left on its old "Own crew"
 * default; the picker now demands an answer (js/core/production-crew.js), so
 * what is left is a wrong answer, an older filing, or hours never filed.
 *
 * A typed Labour cost carries ONE date and no period, so which weeks it pays
 * for is not on file. Dated inside the week, it is taken as that week's
 * labour and nothing is said. Dated later (month-end payroll, or left on the
 * day it was typed) it MAY be what paid for the work — a cost pays for work
 * already done — so the sentence stops claiming "no labour cost" and becomes
 * a note that names the amount and asks. Dated before the week, it cannot be.
 *
 * The test is the JOB's week, not the filer's: time_entries.foreman_id is who
 * filed the sheet, and on a healthy job one person files production while
 * another files hours. Only weeks that have ENDED are flagged — production and
 * timesheet are both weekly and arrive in either order. Known ceiling: a sub's
 * week filed as own crew while the in-house crew also logged hours that week
 * is not caught.
 */
export function uncostedProduction({ reports, profiles, timeEntries, prodLines, entries, today }) {
  const isSub = new Set((profiles || []).filter(p => p.is_subcontractor).map(p => p.id));
  const nameOf = {};
  (profiles || []).forEach(p => { nameOf[p.id] = p.full_name; });
  const covered = new Set();
  (timeEntries || []).forEach(e => { if (e.work_date && hoursOf(e) > 0) covered.add(weekMondayOf(e.work_date)); });
  const typed = (entries || []).filter(e => e.category === 'labour' && e.incurred_on && num(e.amount) > 0);
  typed.forEach(e => covered.add(weekMondayOf(e.incurred_on)));
  const lines = linesByReport(prodLines);

  const byCrew = {};
  (reports || []).forEach(r => {
    if (!r.report_date || isSub.has(r.foreman_id)) return;
    const monday = weekMondayOf(r.report_date);
    if (!monday || covered.has(monday) || !(addDays(monday, 6) < today)) return;
    const items = reportItems(r, lines[r.id]);
    if (!items.length) return;
    const later = typed.some(e => e.incurred_on >= monday);
    const k = (later ? 'typed|' : '') + (r.foreman_id || 'name:' + (r.foreman_name || ''));
    const g = byCrew[k] || (byCrew[k] = {
      who: nameOf[r.foreman_id] || r.foreman_name || '—', n: 0, from: null, to: null, bag: {}, later, monday });
    g.n += 1;
    widen(g, r.report_date);
    if (monday < g.monday) g.monday = monday;
    addItems(g.bag, items);
  });
  return Object.values(byCrew).map(g => {
    const params = { who: g.who, n: g.n, from: g.from, to: g.to, items: itemList(g.bag) };
    if (!g.later) return { key: g.n === 1 ? 'jc.warnUncostedOne' : 'jc.warnUncostedMany',
                           severity: 'warn', group: 'labour', params };
    // What was typed from the first of those weeks on — the money that may cover them.
    const amount = typed.filter(e => e.incurred_on >= g.monday).reduce((s, e) => s + num(e.amount), 0);
    return { key: g.n === 1 ? 'jc.warnUncostedTypedOne' : 'jc.warnUncostedTypedMany',
             severity: 'info', group: 'labour', params: { ...params, amount } };
  });
}

/**
 * What the subcontract rows already know, said out loud: work with no rate, a
 * sub who is also on the timesheet, and reports whose two copies of the
 * quantity disagree. `subRows` are _jcComputeSubLabour's.
 *
 * The no-rate sentence NAMES the item — the catalogue's own name and unit —
 * because the rate is typed per production item (Job Costs → Sub rates), so
 * "which one" is the whole instruction. Only a report filed in the retired
 * format has no item to name, and prints its category label instead.
 */
export function subWarnings(subRows) {
  const out = [];
  const asItem = (p) => p.unitLabel ? { qty: p.qty, labelKey: p.unitLabel } : { qty: p.qty, name: p.label, unit: p.unit };
  (subRows || []).forEach(r => {
    // "When" is when THOSE items were reported; the crew's own first and last
    // day only when the parts carry no dates of their own.
    const base = (parts) => {
      const g = { from: null, to: null };
      parts.forEach(p => { if (p.from) widen(g, p.from); if (p.to) widen(g, p.to); });
      return { who: r.name, from: g.from || r.from || null, to: g.to || r.to || null };
    };
    const unset = (r.parts || []).filter(p => p.rate == null);
    if (unset.length) out.push({ key: 'jc.warnSubNoRate', severity: 'warn', group: 'labour',
      params: { ...base(unset), items: unset.map(asItem) } });
    // Two doors (see _jcComputeSubLabour): hours logged to HIM are already in
    // Labour, so the work may be paid twice; a sheet he only FILED for other
    // people charges nothing by itself. The first is the one that costs money,
    // so it wins when both are true.
    if (r.alsoOnTimesheet) out.push({
      key: num(r.timesheetHours) > 0 ? 'jc.warnSubAlsoHours' : 'jc.warnSubFiledHours',
      severity: 'warn', group: 'labour', params: { who: r.name } });
    const byUnit = {};
    (r.mismatch || []).forEach(m => {
      const g = byUnit[m.unitLabel] || (byUnit[m.unitLabel] = { n: 0, from: null, to: null, onReport: 0, inItems: 0 });
      g.n += 1; g.onReport += num(m.onReport); g.inItems += num(m.inItems);
      if (m.date) widen(g, m.date);
    });
    Object.entries(byUnit).forEach(([labelKey, g]) => out.push({
      key: g.n === 1 ? 'jc.warnSubQtyMismatchOne' : 'jc.warnSubQtyMismatchMany',
      severity: 'warn', group: 'labour',
      params: { who: r.name, n: g.n, from: g.from, to: g.to,
                reported: [{ qty: g.onReport, labelKey }], counted: [{ qty: g.inItems, labelKey }] },
    }));
  });
  return out;
}

/** Hours that cost nothing because the trade (or the worker) has no loaded rate. */
export function unratedHours(labourRows) {
  return (labourRows || [])
    .filter(r => r.loaded == null && num(r.reg) + num(r.ot) > 0)
    .map(r => ({
      key: r.trade ? 'jc.warnUnratedHours' : 'jc.warnUnratedNoTrade',
      severity: 'warn', group: 'labour',
      params: { what: r.trade || '', hours: num(r.reg) + num(r.ot), n: num(r.workers) },
    }));
}

/**
 * One person, one day, more than DAY_HOURS_LIMIT hours ON THIS JOB. A split
 * day is several rows (and spread sick pay re-adds to its own day), so the
 * hours are summed per worker and day before the test. Known ceiling: only one
 * job's entries are in hand, so a day split across two jobs is not caught.
 */
export function suspectDays({ timeEntries, workers }) {
  const byDay = {};
  (timeEntries || []).forEach(e => {
    if (!e.worker_id || !e.work_date) return;
    const k = e.worker_id + '|' + e.work_date;
    byDay[k] = (byDay[k] || 0) + hoursOf(e);
  });
  const nameOf = {};
  (workers || []).forEach(w => { nameOf[w.id] = w.name; });
  const byWorker = {};
  Object.entries(byDay).forEach(([k, h]) => {
    if (!(h > DAY_HOURS_LIMIT)) return;
    const [workerId, date] = k.split('|');
    const g = byWorker[workerId] || (byWorker[workerId] = { who: nameOf[workerId] || '—', n: 0, from: null, to: null, hours: 0 });
    g.n += 1;
    widen(g, date);
    if (h > g.hours) g.hours = h;
  });
  return Object.values(byWorker).map(g => ({
    key: g.n === 1 ? 'jc.warnLongDayOne' : 'jc.warnLongDayMany',
    severity: 'warn', group: 'labour',
    params: { who: g.who, n: g.n, from: g.from, to: g.to, hours: g.hours, limit: DAY_HOURS_LIMIT },
  }));
}

// A typed rental as the span it is on the job: [start, end or today].
const rentalSpans = (rows, today) => (rows || [])
  .filter(r => r.start_date && r.start_date <= today && String(r.label || '').trim())
  .map(r => ({ id: r.id, label: String(r.label).trim(), key: String(r.label).trim().toLowerCase(),
               start: r.start_date, end: r.end_date && r.end_date < today ? r.end_date : today,
               rawEnd: r.end_date || null, daily: num(r.monthly_rate) / DAYS_PER_MONTH }));

/**
 * The same rental on the job twice at once: same name (trimmed, any case) and
 * date ranges that intersect, each taken as [start, end or today].
 *
 * A rental has no unit id to match on — the label is all there is — and two
 * rows with one generic name ("Propane tank") can be two real machines. So
 * this only ever asks the question; `amount` is what the shared days cost at
 * the cheaper of the two rates, i.e. the least that is doubled if it is one.
 */
export function equipmentOverlaps(rows, today) {
  const list = rentalSpans(rows, today);
  const pairs = [];
  for (let i = 0; i < list.length; i++) {
    for (let j = i + 1; j < list.length; j++) {
      const a = list[i], b = list[j];
      if (a.key !== b.key) continue;
      const from = a.start > b.start ? a.start : b.start;
      const to = a.end < b.end ? a.end : b.end;
      if (from > to) continue;
      const days = dayNum(to) - dayNum(from) + 1;
      pairs.push({ a: a.id, b: b.id, what: a.label, from, to, days, amount: days * Math.min(a.daily, b.daily) });
    }
  }
  return pairs;
}

/**
 * equipmentOverlaps' pairs as sentences — ONE per name, however many rows
 * share it. Three rows called "Scissor lift" are three pairs, and three
 * sentences each quoting the same days added up to more than the rows cost.
 *
 * Rows chained by overlapping pairs are one group (a–b and b–c → a, b, c);
 * rows of the same name that overlap nothing are not in it. `amount` is
 * counted per day: on each day two or more of the group are on the job, every
 * daily rate but the dearest — what would go away if they are all one
 * machine. For two rows that is the shared days at the cheaper rate, as
 * before. `rows` carries every id in the group, for the statement's tags.
 */
export function overlapWarnings(rows, today) {
  const byId = {};
  rentalSpans(rows, today).forEach(r => { byId[r.id] = { ...r, s: dayNum(r.start), e: dayNum(r.end) }; });
  const groupOf = {};
  equipmentOverlaps(rows, today).forEach(p => {
    const g = groupOf[p.a] || (groupOf[p.a] = new Set([p.a]));
    [...(groupOf[p.b] || [p.b])].forEach(id => { g.add(id); groupOf[id] = g; });
  });
  return [...new Set(Object.values(groupOf))].map(g => {
    const rs = [...g].map(id => byId[id]);
    const span = { from: null, to: null };
    let days = 0, amount = 0;
    const last = Math.max(...rs.map(r => r.e));
    for (let d = Math.min(...rs.map(r => r.s)); d <= last; d++) {
      const on = rs.filter(r => r.s <= d && d <= r.e).map(r => r.daily);
      if (on.length < 2) continue;
      days += 1;
      amount += on.reduce((s, x) => s + x, 0) - Math.max(...on);
      widen(span, addDays('1970-01-01', d));
    }
    const n = rs.length;
    return {
      key: n > 2 ? 'jc.warnEquipOverlapGroup' : days === 1 ? 'jc.warnEquipOverlapDay' : 'jc.warnEquipOverlap',
      severity: 'warn', group: 'equipment',
      params: { what: rs[0].label, n, from: span.from, to: span.to, days, amount },
      rows: rs.map(r => r.id),
      // The rows as typed — the end NOT clipped to today, so days passing move
      // nothing here and an edit of a date or a rate does.
      basis: rs.map(r => fact(r.id, r.start, r.rawEnd, r.daily)),
    };
  });
}

/**
 * Scaffold on the job through BOTH doors at once: a typed rental the admin
 * marked as scaffold (equipment_costs.kind) that is still on rent, and pieces
 * placed on the same site from the rental inventory that carry a price.
 * Both land in Equipment, and nothing else compares them — Material has its
 * "recipe or deliveries, never both" gate; scaffold has only this question.
 *
 * It only ASKS: a tower rented as a package beside loose frames from another
 * supplier is two real costs. `amount` is the least that is doubled if it is
 * the same gear — per day both are on the job, the cheaper of the two daily
 * figures — and is 0 when they have not shared a billed day yet (then the
 * sentence carries no figure).
 *
 * A piece counts when it has a quantity, a daily rate and a start date, and
 * either is still on site or left after the first typed scaffold rental
 * arrived. `scaffoldRows` are the job's rental-inventory rows (quantity,
 * daily_rate, start_date, end_date, name); an inventory row bills the days
 * BETWEEN its dates (scaffold-rental.js), a typed rental bills both ends.
 * `typed` / `rented` carry the row ids, for the statement's tags — not `rows`,
 * which is the same-name overlap's field.
 */
export function scaffoldTwoDoors({ equipment, scaffoldRows, today }) {
  const typed = (equipment || []).filter(r => r.kind === 'scaffold' && r.start_date && r.start_date <= today
    && (!r.end_date || r.end_date >= today));
  if (!typed.length) return [];
  const first = typed.reduce((m, r) => (r.start_date < m ? r.start_date : m), typed[0].start_date);
  const rented = (scaffoldRows || [])
    .filter(r => num(r.quantity) > 0 && num(r.daily_rate) > 0 && r.start_date && r.start_date <= today)
    .map(r => ({ id: r.id, start: r.start_date, end: r.end_date && r.end_date < today ? r.end_date : null,
                 daily: num(r.quantity) * num(r.daily_rate),
                 fact: fact('r', r.id, r.start_date, r.end_date, num(r.quantity), num(r.daily_rate)) }))
    .filter(r => !r.end || r.end > first);
  if (!rented.length) return [];
  const span = { from: null, to: null };
  let days = 0, amount = 0;
  for (let d = first; d <= today; d = addDays(d, 1)) {
    const t = typed.filter(r => r.start_date <= d).reduce((s, r) => s + num(r.monthly_rate) / DAYS_PER_MONTH, 0);
    const p = rented.filter(r => r.start <= d && d < (r.end || today)).reduce((s, r) => s + r.daily, 0);
    if (!(t > 0 && p > 0)) continue;
    days += 1;
    amount += Math.min(t, p);
    widen(span, d);
  }
  return [{
    key: amount > 0 ? 'jc.warnScaffoldTwoDoors' : 'jc.warnScaffoldTwoDoorsBare',
    severity: 'warn', group: 'equipment',
    params: { what: typed.map(r => String(r.label || '').trim()).filter(Boolean).join(', '),
              from: span.from, to: span.to, days, amount },
    typed: typed.map(r => r.id), rented: rented.map(r => r.id),
    basis: [...typed.map(r => fact('t', r.id, r.start_date, r.end_date, num(r.monthly_rate))), ...rented.map(r => r.fact)],
  }];
}

/**
 * The same rented piece on the job in two rows whose dates overlap — the
 * inventory's version of overlapWarnings above (scaffold-rental.js
 * scaffoldOverlaps finds them; an edited start date is the usual way in, and
 * the editor asks first, but an overlap that was confirmed, or saved before it
 * asked, would otherwise sit in the total unseen).
 *
 * ONE sentence for the job, not one per piece: a start date moved on twenty
 * rows at once is one thing to look at. A single piece is named. `rows`
 * carries every allocation id involved, for the statement's tags.
 */
export function scaffoldOverlapWarnings({ scaffoldRows, today }) {
  const hits = scaffoldOverlaps(scaffoldRows, today);
  if (!hits.length) return [];
  const hit = new Set(hits.flatMap(h => h.ids));
  const span = { from: null, to: null };
  hits.forEach(h => { widen(span, h.from); widen(span, h.to); });
  return [{
    key: hits.length === 1 ? 'jc.warnScaffoldOverlapOne' : 'jc.warnScaffoldOverlapMany',
    severity: 'warn', group: 'equipment',
    params: { what: hits.length === 1 ? hits[0].name : '', n: hits.length, from: span.from, to: span.to,
              amount: hits.reduce((s, h) => s + h.amount, 0) },
    rows: hits.flatMap(h => h.ids),
    basis: (scaffoldRows || []).filter(r => r && hit.has(r.id))
      .map(r => fact(r.id, r.start_date, r.end_date, num(r.quantity), num(r.daily_rate))),
  }];
}

/** The newest dated thing anyone reported on the job, never past `today`. */
export function lastActivityDate({ reports, timeEntries, deliveriesRaw, entries, today }) {
  let last = null;
  const see = (d) => { if (d && d <= today && (!last || d > last)) last = d; };
  (reports || []).forEach(r => see(r.report_date));
  (timeEntries || []).forEach(e => see(e.work_date));
  (deliveriesRaw || []).forEach(d => see(localDateStr(d.requested_at)));
  (entries || []).forEach(e => see(e.incurred_on));
  return last;
}

/**
 * A rental with no return date on a job where nothing has been reported for a
 * full month of rent. It may be exactly right (a hoist that stays to the end)
 * — which is why this is 'info' — but it is also what a machine that went back
 * without being closed looks like, and it adds cost every day until someone
 * notices.
 *
 * One sentence per CASE, not per rental: a job that goes quiet with five
 * machines on it is one thing to look at, so they are named together with
 * their amounts added up. The two cases stay apart because they count from
 * different days — "since the last report" is one day for all of them, "since
 * it arrived" is each rental's own, so that sentence gives no day count.
 */
export function idleRentals({ equipment, lastActivity, today }) {
  const idle = (equipment || [])
    .filter(r => r.start_date && !r.end_date && r.start_date <= today && num(r.monthly_rate) > 0)
    .map(r => {
      // Quiet since the last report — or, when nothing has been reported
      // since the machine arrived, since the day it arrived.
      const after = !!lastActivity && lastActivity > r.start_date;
      const since = after ? lastActivity : r.start_date;
      const days = dayNum(today) - dayNum(since);
      if (days < IDLE_RENTAL_DAYS) return null;
      return { after, basis: fact(r.id, r.start_date, num(r.monthly_rate)),
               what: String(r.label || '').trim(), from: r.start_date, date: after ? lastActivity : null, days,
               amount: days * num(r.monthly_rate) / DAYS_PER_MONTH };
    }).filter(Boolean);
  const say = (key, params, g) => ({ key, severity: 'info', group: 'equipment', params, basis: g.map(x => x.basis) });
  return [true, false].flatMap(after => {
    const g = idle.filter(x => x.after === after);
    if (g.length < 2) {
      return g.map(x => {
        const { after: _after, basis: _basis, ...params } = x;
        return say(after ? 'jc.warnRentalIdle' : 'jc.warnRentalIdleNever', params, [x]);
      });
    }
    // Names as ONE string: formatWarning prints an array as "qty unit name".
    const many = { what: g.map(x => x.what).join(', '), n: g.length, amount: g.reduce((s, x) => s + x.amount, 0) };
    return [after ? say('jc.warnRentalIdleMany', { ...many, date: lastActivity, days: g[0].days }, g)
                  : say('jc.warnRentalIdleNeverMany', many, g)];
  });
}

/**
 * Production whose DATE is suspect: dated after the day it was filed (the
 * field app refuses that since v632, so these are older filings that usually
 * belong to the week before), or two reports from one crew on one day that
 * carry the SAME items and quantities. A second report on a day is not
 * suspect by itself — adding to a filed week is a designed path, and two
 * filings with different quantities are two pieces of work — so only an
 * identical pair is asked about, and the sentence states the quantity. A
 * report with nothing in it never matches another. Neither changes job-to-date
 * money by itself; the first moves it between periods, and a doubled day
 * doubles what it earns and costs.
 */
export function productionDating({ reports, profiles, timeZone, prodLines }) {
  const nameOf = {};
  (profiles || []).forEach(p => { nameOf[p.id] = p.full_name; });
  const crewOf = (r) => r.foreman_id || 'name:' + (r.foreman_name || '');
  const whoOf = (r) => nameOf[r.foreman_id] || r.foreman_name || '—';
  const lines = linesByReport(prodLines);
  const early = {}, perDay = {};
  (reports || []).forEach(r => {
    if (!r.report_date) return;
    const filed = dateInZone(r.created_at, timeZone);
    if (filed && r.report_date > filed) {
      const g = early[crewOf(r)] || (early[crewOf(r)] = { who: whoOf(r), n: 0, from: null, to: null });
      g.n += 1; widen(g, r.report_date);
    }
    const bag = {};
    addItems(bag, reportItems(r, lines[r.id]));
    const same = Object.keys(bag).sort().map(id => id + ':' + bag[id].qty).join('|');
    if (!same) return;
    const k = crewOf(r) + '|' + r.report_date + '|' + same;
    const d = perDay[k] || (perDay[k] = { crew: crewOf(r), who: whoOf(r), date: r.report_date, n: 0, bag });
    d.n += 1;
  });
  const twice = {};
  Object.values(perDay).filter(d => d.n > 1).forEach(d => {
    const g = twice[d.crew] || (twice[d.crew] = { who: d.who, n: 0, from: null, to: null, bag: {} });
    g.n += 1; widen(g, d.date);
    addItems(g.bag, Object.values(d.bag));      // what ONE of the identical reports says
  });
  const out = (map, one, many) => Object.values(map).map(g => ({
    key: g.n === 1 ? one : many, severity: 'info', group: 'contract',
    params: { who: g.who, n: g.n, from: g.from, to: g.to, ...(g.bag ? { items: itemList(g.bag) } : {}) } }));
  return [...out(early, 'jc.warnProdEarlyOne', 'jc.warnProdEarlyMany'),
          ...out(twice, 'jc.warnProdSameDayOne', 'jc.warnProdSameDayMany')];
}

/**
 * A day of production far above what the company has on file for that item —
 * the shape of a unit read wrong (bricks counted into a square-foot item,
 * 2026-10-30). Each reported day of THIS job is held against every OTHER
 * crew-week of the same item across the company, with the field form's
 * numbers (js/core/production-outliers.js) — but BOTH tests must hold: above
 * the highest day filed BEFORE its week AND above MEDIAN_FACTOR × the usual
 * day. (Before its week: a misread kept up for two weeks would otherwise be
 * each week's own record to beat, and hide itself.) The field asks on
 * either, once, at filing; here a note cannot be answered and stays, and on
 * either test alone every item's record day (375 against a best of 374) and a
 * big crew's normal weeks would be listed forever. 'info': the owner decides.
 *
 * `orgLines` are production_line_items of the whole company; their report's
 * date and crew come from the embedded production_reports row when it carries
 * them, else from `orgReports` / `reports` by production_report_id.
 */
export function productionOutliers({ reports, prodLines, profiles, orgLines, orgReports }) {
  if (!orgLines || !orgLines.length || !prodLines || !prodLines.length) return [];
  const nameOf = {};
  (profiles || []).forEach(p => { nameOf[p.id] = p.full_name; });
  const repById = {};
  [...(orgReports || []), ...(reports || [])].forEach(r => { if (r && r.id) repById[r.id] = r; });
  const facts = (l) => {
    const r = repById[l.production_report_id] || {};
    const e = l.production_reports || {};
    const date = e.report_date || r.report_date || null;
    const crew = e.foreman_id || r.foreman_id || 'name:' + (r.foreman_name || '');
    return { date, r, cw: crew + '|' + (date ? weekMondayOf(date) : '') };
  };
  const byItem = {};
  orgLines.forEach(l => {
    const q = num(l.quantity);
    if (!(q > 0) || !l.material_type_id) return;
    const f = facts(l);
    (byItem[l.material_type_id] ||= []).push({ q, cw: f.cw, date: f.date });
  });
  // ponytail: one pass over the item's history per crew-week of this job;
  // pre-sort per item if a company ever has tens of thousands of days.
  const stats = {};
  const out = [];
  prodLines.forEach(l => {
    const q = num(l.quantity);
    const hist = byItem[l.material_type_id];
    if (!(q > 0) || !hist) return;
    const f = facts(l);
    if (!f.date) return;
    const k = l.material_type_id + '|' + f.cw;
    if (!stats[k]) {
      const others = hist.filter(h => h.cw !== f.cw);
      const mon = weekMondayOf(f.date);
      stats[k] = { prior: dayStats(others.filter(h => h.date && h.date < mon).map(h => h.q)),
        usual: dayStats(others.map(h => h.q)) };
    }
    const { prior, usual } = stats[k];
    if (outlierOf(q, prior) !== 'max' || !(usual.median > 0 && q > MEDIAN_FACTOR * usual.median)) return;
    out.push({ key: 'jc.warnProdOutlier', severity: 'info', group: 'contract',
      params: { who: nameOf[f.r.foreman_id] || f.r.foreman_name || '—', from: f.date,
        items: [{ qty: q, name: l.material_types?.name || '', unit: l.material_types?.unit || '' }],
        max: prior.max, median: usual.median, factor: MEDIAN_FACTOR } });
  });
  return out.sort((a, b) => (a.params.from < b.params.from ? -1 : a.params.from > b.params.from ? 1 : 0));
}

/**
 * Percent lines still at 0% on a job that has kept reporting work.
 *
 * A percent line earns contract amount × the percent someone TYPES, so a line
 * nobody has touched earns nothing however much work is done. "Last updated"
 * is the newest contract_line_progress row (written only when the percent
 * itself changes — updated_at also moves on a rename, which must not silence
 * this), falling back to the day the line was created; the backfill sentinel
 * is skipped. No day threshold: the trigger is dated work reported AFTER that
 * day. A change order that has not started yet trips it too, which is why this
 * is 'info'. With `asOf` (a period view) only history up to that day counts.
 * A line finished by `today` (the run's own day) earns its whole contract, so
 * its 0% understates nothing and it is left out.
 */
export function stalePercentLines({ contractLines, progressRows, lastActivity, asOf, today }) {
  if (!lastActivity) return [];
  const lastSet = {};
  (progressRows || []).forEach(p => {
    if (!p.set_on || p.set_on === EPOCH || (asOf && p.set_on > asOf)) return;
    if (!lastSet[p.line_id] || p.set_on > lastSet[p.line_id]) lastSet[p.line_id] = p.set_on;
  });
  const stale = (contractLines || []).filter(l =>
    l.earn_method !== 'quantity' && !l.continues_line_id
    && num(l.contract_amount) > 0 && num(l.percent_complete) === 0 && !finishedBy(l, today))
    .map(l => ({ line: l, since: lastSet[l.id] || localDateStr(l.created_at) }))
    .filter(x => x.since && (!asOf || x.since <= asOf) && lastActivity > x.since);
  if (!stale.length) return [];
  return [{
    key: stale.length === 1 ? 'jc.warnStalePctOne' : 'jc.warnStalePctMany',
    severity: 'info', group: 'contract',
    params: {
      what: stale.length === 1 ? (stale[0].line.description || '') : '',
      n: stale.length,
      amount: stale.reduce((s, x) => s + num(x.line.contract_amount), 0),
      since: stale.reduce((m, x) => (!m || x.since < m ? x.since : m), null),
      date: lastActivity,
    },
  }];
}

/**
 * Material the total understates, or cannot see at all. Material is what came
 * in (owner, 2026-11-02): the arrived deliveries and pickups, priced, plus the
 * costs typed under Material — on every project, so there is one road and its
 * holes are the only ones. `deliveries` are jcComputeProject's priced orders:
 *
 *   NO PRICE ON FILE  an arrived item with no price costs $0 until it is priced
 *                     or the invoice is typed (how many orders, and which items)
 *   TWO PRICES        a name that fits several catalogue rows at different
 *                     prices is not priced at all (see extraMaterialPrice in
 *                     cost-recipe.js)
 *   NOT MARKED DELIVERED (2026-10-02) — only what arrived is money, and
 *                     "arrived" is a status somebody has to set. An order past
 *                     its needed-by day that is still requested / on schedule
 *                     costs $0 and is the likeliest way this road understates.
 *                     On hold and cancelled are deliberate and are not asked
 *                     about. `today` is the caller's (the as-of day in a period).
 *
 * An item the job set to "Not a cost" is cut out of `d.est` before it gets
 * here, so it is in no count and in no late order's amount.
 *
 * The recipe is no money any more, so nothing it lacks is a hole in a total:
 * its sentences (no recipe, a component with no amount or no price, freight
 * counted twice, no recipe line yet, delivered items in no recipe line) went
 * with it. The "It's correct" checks written for them match nothing now —
 * partitionAcked hides and counts only what the list still holds.
 */
export function materialWarnings({ deliveries, today }) {
  const out = [];
  // `basis`: what a count-only sentence counted, so a different record behind
  // the same count is a different fingerprint (warningFingerprint).
  const warn = (key, params, basis) => out.push({ key, severity: 'warn', group: 'material', params,
    ...(basis ? { basis } : {}) });
  const plural = (n, one, many) => (n === 1 ? one : many);
  const isLate = (d) => d.status !== 'on_hold' && d.status !== 'cancelled' && d.needed_by && today && d.needed_by < today;
  const notDelivered = (late, amount) => {
    if (!late.length) return;
    warn(amount > 0 ? plural(late.length, 'jc.warnNotDeliveredOne', 'jc.warnNotDeliveredMany')
                    : plural(late.length, 'jc.warnNotDeliveredBareOne', 'jc.warnNotDeliveredBareMany'),
      { n: late.length, amount, since: late.reduce((m, d) => (!m || d.needed_by < m ? d.needed_by : m), null) },
      late.map(d => fact(d.id, d.needed_by)));
  };
  // An order whose every entry is "not a cost" (d.excluded, cut out of d.est)
  // would bring nothing that counts: it is not asked about.
  const allOut = (d) => (d.excluded || []).length > 0 && !((d.est && d.est.lines) || []).length && !((d.est && d.est.unpriced) || []).length;
  const late = (deliveries || []).filter(d => d.onOrder && isLate(d) && !allOut(d));
  notDelivered(late, late.reduce((s, d) => s + num(d.est && d.est.total), 0));
  // An order that has not arrived costs nothing yet, so an item on it with no
  // price is not a hole in the total — it is asked about once it counts.
  // An item the catalogue does not hold (ids: [] — rented scaffold requested
  // through Logistics, a row deleted since) is not asked about: Rates & Defaults
  // cannot price it, and scaffold billed on Rentals would then count twice. Its
  // statement row says what it is instead (66-jobcosts.js).
  const priceable = (d) => ((d.est && d.est.unpriced) || []).filter(l => !(Array.isArray(l.ids) && !l.ids.length));
  const open = (deliveries || []).filter(d => !d.onOrder && !d.invoice && priceable(d).length);
  if (open.length) warn(plural(open.length, 'jc.warnDeliveryNoPriceOne', 'jc.warnDeliveryNoPriceMany'), { n: open.length },
    open.map(d => fact(d.id, ...priceable(d).map(l => `${l.label}:${num(l.qty)}`).sort())));
  const twins = {};
  open.forEach(d => priceable(d).filter(l => l.ambiguous).forEach(l => {
    const k = String(l.label).trim().toLowerCase() + '|' + (l.unit || '');
    const g = twins[k] || (twins[k] = { qty: 0, unit: l.unit || '', name: l.label });
    g.qty += num(l.qty);
  }));
  if (Object.keys(twins).length) warn('jc.warnExtraAmbiguous', { items: Object.values(twins) });
  return out;
}

/**
 * Every warning for one job, in reading order: material, labour, equipment,
 * then the earned side. `input` is jcComputeProject's own (already clipped)
 * feeds plus the rows it just priced — see that function for the call.
 */
export function buildCostWarnings(input) {
  const i = input || {};
  const today = i.today;
  const lastActivity = lastActivityDate({ ...i, today });
  return [
    ...(i.material ? materialWarnings({ ...i.material, today }) : []),
    ...uncostedProduction({ ...i, today }),
    ...subWarnings(i.subRows),
    ...unratedHours(i.labourRows),
    ...suspectDays(i),
    ...overlapWarnings(i.equipment, today),
    ...scaffoldTwoDoors({ equipment: i.equipment, scaffoldRows: i.scaffoldRows, today }),
    ...scaffoldOverlapWarnings({ scaffoldRows: i.scaffoldRows, today }),
    ...idleRentals({ equipment: i.equipment, lastActivity, today }),
    ...productionDating(i),
    ...productionOutliers(i),
    ...stalePercentLines({ ...i, lastActivity }),
  ];
}

/**
 * Rented equipment the Equipment total cannot price. Job Costs says these on
 * the group's gauge (and gives each piece its own statement row), so they are
 * NOT part of buildCostWarnings' list — there they would print twice. They are
 * handed out separately (summary.warnings.gauge) for the screens that have no
 * gauge: the P&L statement and its PDF.
 *
 * `pieces` is scaffoldRental.unpricedPieces' answer over the job's unpriced
 * rental rows — PIECES, not date ranges, the same count the gauge prints.
 */
export function rentalGapWarnings({ hasSiteCode, pieces }) {
  const out = [];
  const warn = (key, params, basis) => out.push({ key, severity: 'warn', group: 'equipment', params: params || {},
    ...(basis ? { basis } : {}) });
  if (!hasSiteCode) warn('jc.warnRentalNoSiteCode');
  // The pieces by name, as unpricedPieces counts them: another piece behind
  // the same count is another fingerprint.
  const say = (names, one, named, many) => {
    if (names.length === 1) { if (names[0]) warn(named, { what: names[0] }); else warn(one, {}, names); }
    else if (names.length > 1) warn(many, { n: names.length }, names);
  };
  const p = pieces || {};
  say(p.noRate || [], 'jc.warnRentalNoPriceOne', 'jc.warnRentalNoPriceNamed', 'jc.warnRentalNoPriceMany');
  say(p.noDate || [], 'jc.warnRentalNoDateOne', 'jc.warnRentalNoDateNamed', 'jc.warnRentalNoDateMany');
  return out;
}

/**
 * Installed quantity nobody is paying for, one entry PER PRODUCT, each in its
 * own unit — never one sum: 50 sf of veneer and 30 blocks are not "80 count".
 *
 * `alloc` is job-value.js allocateInstalled's answer. It also reports, as
 * overage, the whole installed quantity of a product NO quantity line names
 * (a job with no contract lines, or one billed by percent that still reports
 * production). That is not "beyond approved scope" — there is no scope to be
 * beyond — so only products that own a quantity line are kept, which is the
 * set the Contract tab prints its own per-line overage for.
 */
export function overageItems(alloc, typeById) {
  const a = alloc || {};
  const onContract = new Set(Object.values(a.typeByLineId || {}).map(String));
  return Object.entries(a.overageByType || {})
    .filter(([id, qty]) => onContract.has(String(id)) && num(qty) > 0)
    .map(([id, qty]) => {
      const t = (typeById || {})[id] || {};
      return { name: t.name || '', unit: t.unit || '', qty: num(qty) };
    });
}

/** Where on a statement a warning's money sits, in reading order. */
const GROUP_ORDER = ['material', 'labour', 'equipment', 'trucking', 'contract'];

/**
 * Everything worth checking before reading ONE job's P&L, as the same plain
 * { key, severity, group, params } objects — so the statement, its PDF, the
 * count on the "Every job" table and a mother's "N sub-projects have items
 * worth checking" line are all the same list, and cannot disagree.
 *
 * `warnings` is jcComputeProject's summary.warnings:
 *   list           buildCostWarnings' answer (what Job Costs prints in words)
 *   gauge          rentalGapWarnings' (what Job Costs prints on a gauge)
 *   overage        overageItems'
 *   unpricedCount  quantity lines with no rate — they earn nothing
 *   unpricedIds    those lines' ids — what the count counted, for its fingerprint
 *   generalShare   0-1, the share of direct cost filed under no task, and
 *   generalTotal   the same in dollars — printed first, because a share that
 *                  rounds to "0%" still has an amount. Left out when the job
 *                  has no contract lines (`noContract`): with no task to file
 *                  under it is 100% by construction.
 */
export function statementWarnings(warnings, opts) {
  const w = warnings || {};
  const earnedSide = [];
  const add = (key, severity, params, basis) => earnedSide.push({ key, severity, group: 'contract', params,
    ...(basis ? { basis } : {}) });
  (w.overage || []).forEach(it => add('pnl.warnOverage', 'warn', { items: [it] }));
  const n = num(w.unpricedCount);
  if (n > 0) add(n === 1 ? 'pnl.warnUnpricedOne' : 'pnl.warnUnpricedMany', 'warn', { n }, (w.unpricedIds || []).map(String));
  if (num(w.generalShare) > 0 && !(opts && opts.noContract)) {
    add('pnl.warnGeneralShare', 'info', { amount: num(w.generalTotal), pct: num(w.generalShare) });
  }
  const rank = (x) => { const i = GROUP_ORDER.indexOf(x.group); return i < 0 ? GROUP_ORDER.length : i; };
  // Array.prototype.sort is stable: within a group the order above is kept.
  return [...(w.gauge || []), ...earnedSide, ...(w.list || [])].sort((a, b) => rank(a) - rank(b));
}

const MONEY_PARAMS = new Set(['amount', 'freight']);
const DATE_PARAMS = new Set(['from', 'to', 'date', 'since']);
const QTY_PARAMS = new Set(['max', 'median']);

/**
 * One warning → its sentence. The caller brings its own formatters, so the
 * screen prints the tenant's date and money formats with HTML escaped, and a
 * PDF prints plain text:
 *
 *   f = { t(key, vars), money(n), date('YYYY-MM-DD'), hours(n), qty(n), pct(0-1), esc(s) }
 *
 * Params are formatted by NAME: amount → money; from / to / date / since →
 * date; hours → hours; pct → pct; max / median → qty; an array → a list of "qty unit name" items ("qty × name" for a counted one); any other
 * string → escaped. `{when}` is derived for every sentence: the one day, or
 * "from – to" when the two differ.
 */
export function formatWarning(w, f) {
  const o = f || {};
  const same = (v) => String(v);
  const t = o.t || same, esc = o.esc || same, date = o.date || same, qty = o.qty || same;
  // "count" is the catalogue's word for "it has no unit": printed raw it read
  // "2,029 count 8" Block". A counted item says how many of what — "2,029 × 8"
  // Block" — and every other unit prints as typed ("50 sf Panel B").
  const item = (it) => {
    if (it.labelKey) return [qty(it.qty), t(it.labelKey)].filter(Boolean).join(' ');
    const counted = /^count$/i.test(it.unit || '');
    const what = [counted ? '' : it.unit, it.name].filter(Boolean).map(esc).join(' ');
    return [qty(it.qty), what].filter(Boolean).join(counted && it.name ? ' × ' : ' ');
  };
  const p = (w && w.params) || {};
  const vars = {};
  Object.entries(p).forEach(([k, v]) => {
    if (v == null) vars[k] = '';
    else if (Array.isArray(v)) vars[k] = v.map(item).join(', ');
    else if (MONEY_PARAMS.has(k)) vars[k] = (o.money || same)(v);
    else if (DATE_PARAMS.has(k)) vars[k] = date(v);
    else if (k === 'hours') vars[k] = (o.hours || same)(v);
    else if (k === 'pct') vars[k] = (o.pct || same)(v);
    else if (QTY_PARAMS.has(k)) vars[k] = qty(v);
    else if (typeof v === 'string') vars[k] = esc(v);
    else vars[k] = String(v);
  });
  if (p.from) vars.when = p.to && p.to !== p.from ? `${date(p.from)} – ${date(p.to)}` : date(p.from);
  return t(w.key, vars);
}

// ── Checked warnings (review D9) ─────────────────────────────────────────────
// A warning someone looked at and answered "it's correct" leaves the active
// list — until the numbers that raised it change, and then it is back by
// itself. Two strings carry that, both derived here so every screen, count and
// PDF gets the same ones:
//
//   warningKey          WHAT it is about: the project, the kind of warning
//                       (One / Many / Bare / Day / Named are wordings of one
//                       kind) and its subject — the person, the item names, the
//                       row ids. Stable while the numbers move.
//   warningFingerprint  the values that raised it: amounts, quantities, dates,
//                       row ids, and `basis` — the raw facts of the records it
//                       counted or measured. A sentence that only says "3 items
//                       have no price" names WHICH three there; an overlap
//                       carries its rows' typed dates and rates. A check is
//                       kept against it, so a changed value no longer matches
//                       and the warning returns.
//
// What only moves because the calendar did is left out of the fingerprint: an
// open rental's overlap grows one day (and one day's rate) every day, a job's
// last report moves with every report, a company's usual day moves whenever any
// crew files. Checked once, those stay checked until a RECORD changes — and the
// record facts in `basis` (never clipped to today) are what catch that.
// Lists (items, ids, basis) are compared as sets; numbers to 4 decimals.

const WORDING = /(Bare)?(One|Many|Day|Named)?$/;
const kindOf = (key) => String(key || '').replace(WORDING, '');
const MOVES_WITH_TIME = {
  'jc.warnEquipOverlap': ['to', 'days', 'amount'],
  'jc.warnEquipOverlapGroup': ['to', 'days', 'amount'],
  'jc.warnScaffoldTwoDoors': ['to', 'days', 'amount'],
  'jc.warnScaffoldOverlap': ['to', 'days', 'amount'],
  'jc.warnRentalIdle': ['days', 'amount'],
  'jc.warnRentalIdleNever': ['days', 'amount'],
  'jc.warnStalePct': ['date'],
  'jc.warnProdOutlier': ['max', 'median'],
  'pnl.warnGeneralShare': ['pct'],
};

/** Canonical text of a value: object keys sorted, nulls dropped, arrays as sets. */
function canon(v) {
  if (Array.isArray(v)) return '[' + v.map(canon).sort().join(',') + ']';
  if (v && typeof v === 'object') {
    return '{' + Object.keys(v).sort().filter(k => v[k] != null)
      .map(k => JSON.stringify(k) + ':' + canon(v[k])).join(',') + '}';
  }
  if (typeof v === 'number') return String(Number.isFinite(v) ? Math.round(v * 1e4) / 1e4 : 0);
  return JSON.stringify(v == null ? null : v);
}
// cyrb53 — a short, well-mixed 53-bit hash. Not a secret: it only keeps the
// stored strings short (a list of 30 item names is still one short token).
function hash(s) {
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 2654435761);
    h2 = Math.imul(h2 ^ c, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}
const idsOf = (w) => [...(w.rows || []), ...(w.typed || []), ...(w.rented || [])].map(String);

/** WHAT a warning is about, for one project. Same inputs → same key. */
export function warningKey(w, projectId) {
  const p = (w && w.params) || {};
  // Every list a sentence names things in (items, reported, counted) — by
  // what they are, never how many.
  const things = Object.values(p).filter(Array.isArray).flat()
    .map(it => (it && (it.labelKey || [it.name, it.unit].join('|'))) || '');
  // A per-day detector names its day: two outlier days of one crew and item
  // are two warnings, and neither shifts when another day comes or goes.
  const day = kindOf(w && w.key) === 'jc.warnProdOutlier' ? p.from : undefined;
  const subject = canon({ who: p.who, what: p.what, things, ids: idsOf(w || {}), day });
  return `${projectId || ''}|${kindOf(w && w.key)}|${hash(subject)}`;
}

/** The values that raised a warning. A changed amount, quantity, date or row → a different fingerprint. */
export function warningFingerprint(w) {
  const x = w || {};
  const skip = new Set(MOVES_WITH_TIME[kindOf(x.key)] || []);
  const params = {};
  Object.entries(x.params || {}).forEach(([k, v]) => { if (!skip.has(k)) params[k] = v; });
  return hash(canon({ kind: kindOf(x.key), severity: x.severity, params, ids: idsOf(x),
    basis: (x.basis || []).map(String) }));
}

/**
 * One project's warnings split by the checks on file. `acks` are rows of
 * cost_warning_acks ({ warning_key, fingerprint, … }) — any project's; the key
 * carries the project. Every warning comes back as a copy with `ackKey` and
 * `ackFp` (what a check of it must store); a checked one also carries `ack`.
 * A check whose fingerprint no longer matches is no check: the warning is active.
 *
 * Two warnings with the same key on one job (two crews with the same name) are
 * told apart by their place in the list.
 * ponytail: by position — if the list reorders, a check can land on its twin and
 * the other one shows again (never the reverse: a changed fingerprint is never
 * hidden). Give such detectors an id of their own if that ever bites.
 */
export function partitionAcked(list, projectId, acks) {
  const byKey = new Map((acks || []).map(a => [a.warning_key, a]));
  const seen = {};
  const active = [], checked = [];
  (list || []).forEach(w => {
    let key = warningKey(w, projectId);
    seen[key] = (seen[key] || 0) + 1;
    if (seen[key] > 1) key += '#' + seen[key];
    const x = { ...w, ackKey: key, ackFp: warningFingerprint(w) };
    const ack = byKey.get(key);
    if (ack && ack.fingerprint === x.ackFp) checked.push({ ...x, ack }); else active.push(x);
  });
  return { active, checked };
}

/** The "N to check" count: unchecked warnings where money is probably missing or doubled. */
export function uncheckedWarnCount(list, projectId, acks) {
  return partitionAcked(list, projectId, acks).active.filter(w => w.severity === 'warn').length;
}
